import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table';

interface BillingInvoice {
  id: string;
  organization_id: string;
  stripe_invoice_id: string;
  amount: number;
  currency: string;
  status: string;
  hosted_invoice_url: string | null;
  invoice_pdf_url: string | null;
  created_at: string;
  organization_name?: string;
  organization_slug?: string;
}

export default function Billing() {
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Fetch all billing history
  const { data: billingHistory, isLoading } = useQuery({
    queryKey: ['billing', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('billing_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Fetch org names for the billing records
      const orgIds = [...new Set((data || []).map((d: any) => d.organization_id).filter(Boolean))];
      let orgMap: Record<string, { name: string; slug: string }> = {};

      if (orgIds.length > 0) {
        const { data: orgs } = await supabase
          .from('organizations')
          .select('id, name, slug')
          .in('id', orgIds as never);

        if (orgs) {
          orgMap = Object.fromEntries(
            (orgs as any[]).map((o) => [o.id, { name: o.name, slug: o.slug }])
          );
        }
      }

      return ((data || []) as any[]).map((invoice) => ({
        ...invoice,
        organization_name: orgMap[invoice.organization_id]?.name || 'Unknown',
        organization_slug: orgMap[invoice.organization_id]?.slug || '',
      })) as BillingInvoice[];
    },
  });

  // Fetch problem subscriptions (past_due or unpaid)
  const { data: problemSubscriptions } = useQuery({
    queryKey: ['billing', 'problems'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_info')
        .select('*')
        .in('status', ['past_due', 'unpaid'] as any);

      if (error) throw error;

      // Get org names
      const orgIds = (data || []).map((d: any) => d.organization_id).filter(Boolean);
      let orgMap: Record<string, { name: string; slug: string }> = {};

      if (orgIds.length > 0) {
        const { data: orgs } = await supabase
          .from('organizations')
          .select('id, name, slug')
          .in('id', orgIds as never);

        if (orgs) {
          orgMap = Object.fromEntries(
            (orgs as any[]).map((o) => [o.id, { name: o.name, slug: o.slug }])
          );
        }
      }

      return ((data || []) as any[]).map((sub) => ({
        ...sub,
        organization_name: orgMap[sub.organization_id]?.name || 'Unknown',
        organization_slug: orgMap[sub.organization_id]?.slug || '',
      }));
    },
  });

  const filteredData = useMemo(() => {
    if (!billingHistory) return [];
    if (statusFilter === 'all') return billingHistory;
    return billingHistory.filter((invoice) => invoice.status === statusFilter);
  }, [billingHistory, statusFilter]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'open':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'void':
      case 'uncollectible':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: 'bg-green-100 text-green-700',
      open: 'bg-yellow-100 text-yellow-700',
      draft: 'bg-gray-100 text-gray-700',
      void: 'bg-red-100 text-red-700',
      uncollectible: 'bg-red-100 text-red-700',
      past_due: 'bg-orange-100 text-orange-700',
      unpaid: 'bg-red-100 text-red-700',
    };
    return (
      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', styles[status] || styles.draft)}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const columns: ColumnDef<BillingInvoice>[] = [
    {
      accessorKey: 'organization_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Client" />
      ),
      cell: ({ row }) => (
        <Link
          to={`/clients/${row.original.organization_slug}`}
          className="font-medium text-gray-900 dark:text-white hover:text-blue-600"
        >
          {row.original.organization_name}
        </Link>
      ),
    },
    {
      accessorKey: 'stripe_invoice_id',
      header: 'Invoice',
      cell: ({ row }) => (
        <p className="text-sm font-mono text-gray-600 dark:text-gray-400">
          {row.original.stripe_invoice_id?.slice(0, 20) || '-'}
        </p>
      ),
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Amount" />
      ),
      cell: ({ row }) => (
        <p className="font-medium text-gray-900 dark:text-white">
          {formatCurrency(row.original.amount, row.original.currency)}
        </p>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(row.original.status)}
          {getStatusBadge(row.original.status)}
        </div>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Date" />
      ),
      cell: ({ row }) => (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(row.original.created_at)}
        </p>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => {
        const invoice = row.original;
        return (
          <div className="flex items-center justify-end gap-2">
            {invoice.hosted_invoice_url && (
              <a
                href={invoice.hosted_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="View in Stripe"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            {invoice.invoice_pdf_url && (
              <a
                href={invoice.invoice_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Download PDF"
              >
                <FileText className="h-4 w-4" />
              </a>
            )}
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Billing</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Track payments and invoices across all clients
        </p>
      </div>

      {/* Problem Subscriptions Alert */}
      {problemSubscriptions && problemSubscriptions.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-red-800 dark:text-red-200">
                {problemSubscriptions.length} subscription{problemSubscriptions.length > 1 ? 's' : ''} need attention
              </h3>
              <div className="mt-2 space-y-2">
                {problemSubscriptions.map((sub: any) => (
                  <div key={sub.id} className="flex items-center justify-between text-sm">
                    <Link
                      to={`/clients/${sub.organization_slug}`}
                      className="text-red-700 dark:text-red-300 hover:underline"
                    >
                      {sub.organization_name}
                    </Link>
                    {getStatusBadge(sub.status)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Filter */}
      <div className="flex items-center gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="paid">Paid</option>
          <option value="open">Open</option>
          <option value="void">Void</option>
          <option value="uncollectible">Uncollectible</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        searchKey="organization_name"
        searchPlaceholder="Search by client..."
      />
    </div>
  );
}
